import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Badge } from '@dreamtree-org/twreact-ui';
import { PageHeader, StatusBadge, SectionCard, QueryState, EmptyState } from '@/components';
import { useQuery } from '@/lib/useQuery';
import { formatDate } from '@/lib/format';
import DataTable from '../components/DataTable';
import FilterBar from '../components/FilterBar';
import { filterMerchants, optionsWithCounts } from '../lib/filters';
import { usePlatformCan } from '../lib/access';

/** Merchant registry (REAL): status / plan filters and search, kept in the URL. */
export default function Merchants() {
  const canManage = usePlatformCan('merchants.manage');
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') || '';
  const status = params.get('status') || 'all';
  const plan = params.get('plan') || 'all';
  const merchants = useQuery('platformMerchants', undefined, { select: (r) => r.data });

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (!value || value === 'all') next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Merchants"
        subtitle="Every merchant has its own slug, domains and isolated database"
        actions={canManage ? <Button onClick={() => nav('/merchants/new')}>Register merchant</Button> : null}
      />
      <QueryState query={merchants}>
        {(all) => {
          const rows = filterMerchants(all, { status, plan, q });
          return (
            <SectionCard>
              <FilterBar
                q={q}
                onQ={(v) => setParam('q', v)}
                placeholder="Name, slug, domain, email, city…"
                filters={[
                  { key: 'status', label: 'Status', value: status, options: optionsWithCounts(all, 'status') },
                  { key: 'plan', label: 'Plan', value: plan, options: optionsWithCounts(all, 'plan_key') },
                ]}
                onFilter={setParam}
              />
              <p className="text-sm opacity-70" aria-live="polite">{rows.length} of {all.length} merchants</p>
              {rows.length ? (
                <DataTable
                  data={rows}
                  pagination={rows.length > 25}
                  onRowClick={(r) => nav(`/merchants/${r.id}`)}
                  columns={[
                    { key: 'display_name', label: 'Merchant', render: (r) => (
                      <div className="min-w-0">
                        <div className="font-medium">{r.display_name}</div>
                        <div className="text-xs opacity-60">{r.slug}</div>
                      </div>
                    ) },
                    { key: 'primary_host', label: 'Primary domain', render: (r) => <span className="break-all">{r.primary_host || '—'}</span> },
                    { key: 'domain_count', label: 'Domains', hideOnMobile: true },
                    { key: 'plan_key', label: 'Plan', render: (r) => <Badge variant="primary">{r.plan_key || '—'}</Badge> },
                    { key: 'database_name', label: 'Database', hideOnMobile: true, render: (r) => <code className="text-xs">{r.database_name}</code> },
                    { key: 'city', label: 'City', hideOnMobile: true },
                    { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
                    { key: 'created_at', label: 'Joined', hideOnMobile: true, render: (r) => formatDate(r.created_at) },
                  ]}
                />
              ) : (
                <EmptyState title="No merchants match" action={<Button variant="outline" onClick={() => setParams({}, { replace: true })}>Clear filters</Button>}>
                  Try a different status, plan or search term.
                </EmptyState>
              )}
            </SectionCard>
          );
        }}
      </QueryState>
    </div>
  );
}
